// pages/students/index.tsx
import {
  createStudentColumns,
  StudentActionType,
} from "@/colDef/studentColDef";
import {
  Button,
  Card,
  CardContent,
  CustomAlertDialog,
  DataTable,
  ErrorMessage,
  PageContainer,
  Pagination,
  Spinner,
} from "@/components";
import Search from "@/components/Search";
import StudentEditDialog from "@/components/formDialog/StudentEditDialog";
import {
  useDeleteStudentDetailMutation,
  useStudentsQuery,
} from "@/hooks/api/student.queries";
import useDebounce from "@/hooks/useDebounce";
import { useAlertDialog } from "@/hooks/useDialogState";
import { usePagination } from "@/hooks/usePagination";
import { StudentParamsType, StudentType } from "@/services/student.service";
import { Filter, Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

const PAGE_SIZE = 10;
// We fetch the full list (paged through in the service) and filter/paginate on
// the client, because the /v1/students endpoint does not honour a `search` param.

const StudentsPage = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const { page, setPage, resetPage } = usePagination({
    initialLimit: PAGE_SIZE,
  });
  const debouncedSearch = useDebounce(searchTerm, 300);

  const deleteDialog = useAlertDialog();
  const [editId, setEditId] = useState<string | null>(null);
  // TODO: Student creation from this page requires a batch selection — use StudentDialog from Batch View instead.

  const filters: StudentParamsType = useMemo(() => ({}), []);

  const { data, isLoading, isFetching, isError, refetch } =
    useStudentsQuery(filters);
  const deleteMutation = useDeleteStudentDetailMutation();

  const allStudents = data?.students ?? [];

  // ─── Client-side search ─────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return allStudents;
    return allStudents.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.rollNo.toLowerCase().includes(q) ||
        s.email.toLowerCase().includes(q) ||
        s.dept.toLowerCase().includes(q)
    );
  }, [allStudents, debouncedSearch]);

  // ─── Client-side pagination ─────────────────────────────────────────────────
  const totalEntries = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalEntries / PAGE_SIZE));
  const students = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const editStudent = useMemo(
    () => allStudents.find((s) => s._id === editId) ?? null,
    [allStudents, editId]
  );

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleDelete = useCallback(() => {
    deleteMutation.mutate(deleteDialog.state.mantraId, {
      onSuccess: () => deleteDialog.close(),
    });
  }, [deleteMutation, deleteDialog]);

  const handleActionClick = useCallback(
    (payload: StudentActionType) => {
      if (payload.action === "delete") {
        deleteDialog.open({
          message: (
            <span>
              Are you sure you want to remove{" "}
              <strong className="text-gray-800">{payload.name}</strong>? This
              action cannot be undone.
            </span>
          ),
          mantraId: payload.id,
          mantraTitle: payload.name,
          action: "delete",
        });
      } else if (payload.action === "edit") {
        setEditId(payload.id);
      }
    },
    [deleteDialog]
  );

  const columns = useMemo(
    () => createStudentColumns(handleActionClick),
    [handleActionClick]
  );

  if (isError) {
    return (
      <ErrorMessage
        message="Failed to load students."
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <PageContainer className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Student Management</h1>
        <div className="flex items-center gap-3">
          {/* Advanced Filters — placeholder for future implementation */}
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={() => {
              /* TODO: open advanced filters panel */
            }}
          >
            <Filter size={15} />
            Advanced Filters
          </Button>

          <Button
            className="flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800"
            onClick={() => {}}
            disabled
          >
            <Plus size={16} />
            Add New
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <Search
          placeholder="Search by name, roll no, dept..."
          value={searchTerm}
          isLoading={isFetching}
          onValueChange={(val) => {
            setSearchTerm(val);
            resetPage();
          }}
          className={{ inputGroupClass: "max-w-xs" }}
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <DataTable<StudentType, any> columns={columns} data={students} />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <Pagination
        page={page}
        totalPages={totalPages}
        totalEntries={totalEntries}
        currentCount={students.length}
        onPageChange={setPage}
      />

      {/* Edit Dialog — auto-fills from the student's detail */}
      {editId && (
        <StudentEditDialog
          isOpen={!!editId}
          onClose={() => setEditId(null)}
          studentId={editId}
          seed={
            editStudent
              ? {
                  name: editStudent.name,
                  email: editStudent.email,
                  phone: editStudent.phone,
                  cgpa: editStudent.cgpa || undefined,
                }
              : undefined
          }
        />
      )}

      {/* Delete Confirmation */}
      <CustomAlertDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="Confirm Deletion"
        description={deleteDialog.state.message}
        confirmText="Yes, Remove"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={deleteDialog.close}
      />
    </PageContainer>
  );
};

export default StudentsPage;
